import React from 'react';
import { useDarkMode } from '../context/DarkModeContext';
import { useDarkClasses } from '../hooks/useDarkClasses';
import { useRoleTheme } from '../hooks/useRoleTheme';
import { SearchIcon, BookIcon, ShoppingBagIcon, UserIcon, ShieldIcon, ChevronRightIcon, PenToolIcon, HeartIcon, CreditCardIcon, WrenchIcon, MailIcon, MessageSquareIcon, UsersIcon } from 'lucide-react';
const HelpCenter = () => {
  const {
    darkMode
  } = useDarkMode();
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  const categories = [{
    name: 'Getting Started',
    icon: BookIcon,
    articles: 12,
    color: 'text-indigo-600 bg-indigo-100 dark:text-indigo-400 dark:bg-indigo-900/30'
  }, {
    name: 'Orders & Shipping',
    icon: ShoppingBagIcon,
    articles: 8,
    color: 'text-blue-600 bg-blue-100 dark:text-blue-400 dark:bg-blue-900/30'
  }, {
    name: 'Account & Profile',
    icon: UserIcon,
    articles: 15,
    color: 'text-emerald-600 bg-emerald-100 dark:text-emerald-400 dark:bg-emerald-900/30'
  }, {
    name: 'Privacy & Security',
    icon: ShieldIcon,
    articles: 6,
    color: 'text-amber-600 bg-amber-100 dark:text-amber-400 dark:bg-amber-900/30'
  }, {
    name: 'For Authors',
    icon: PenToolIcon,
    articles: 24,
    color: 'text-purple-600 bg-purple-100 dark:text-purple-400 dark:bg-purple-900/30'
  }, {
    name: 'For Readers',
    icon: HeartIcon,
    articles: 18,
    color: 'text-pink-600 bg-pink-100 dark:text-pink-400 dark:bg-pink-900/30'
  }, {
    name: 'Billing & Payments',
    icon: CreditCardIcon,
    articles: 10,
    color: 'text-cyan-600 bg-cyan-100 dark:text-cyan-400 dark:bg-cyan-900/30'
  }, {
    name: 'Technical Support',
    icon: WrenchIcon,
    articles: 9,
    color: 'text-gray-600 bg-gray-100 dark:text-gray-400 dark:bg-gray-800'
  }];
  const popularArticles = [{
    title: 'How do I track my order?',
    category: 'Orders & Shipping'
  }, {
    title: 'Return and refund policy',
    category: 'Orders & Shipping'
  }, {
    title: 'Setting up your author profile',
    category: 'For Authors'
  }, {
    title: 'Managing subscriptions',
    category: 'Account & Profile'
  }, {
    title: 'Payment methods accepted',
    category: 'Billing & Payments'
  }, {
    title: 'How do I upload my book?',
    category: 'For Authors'
  }, {
    title: 'Finding books by trope or genre',
    category: 'For Readers'
  }, {
    title: 'How does the review system work?',
    category: 'For Readers'
  }];
  return <div className={`min-h-screen pb-20 ${dk.page}`} data-id="element-9985" data-mp="1lh5525pktwpa">
      {/* Hero Search */}
      <div className={`py-16 border-b ${dk.surface} ${dk.border}`} data-id="element-9986" data-mp="1hcxctyjwtymb">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-9987" data-mp="1cf49redwlkq9">
          <h1 className={`text-3xl md:text-4xl font-bold mb-4 ${dk.textHeading}`} data-id="element-9988" data-mp="1ch8a5mclg1ls">
            How can we help you?
          </h1>
          <p className={`text-lg mb-8 ${dk.textSecondary}`} data-id="element-9989" data-mp="3t09c9dbm8iul">
            Search across 200+ help articles, guides, and tutorials.
          </p>
          <div className="max-w-2xl mx-auto relative" data-id="element-9990" data-mp="vowewrjj2rc">
            <SearchIcon className={`absolute left-5 top-4 h-6 w-6 ${dk.textMuted}`} data-id="element-9991" data-mp="5vjkksjxle8p" />
            <input type="text" placeholder="Search for answers..." className={`w-full pl-14 pr-6 py-4 rounded-full border shadow-sm text-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition-all ${dk.input}`} data-id="element-9992" data-mp="35dawyiz8fgy9" />
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-id="element-9993" data-mp="2lnqppcysmgsc">
        {/* Categories Grid */}
        <div className="mb-16" data-id="element-9994" data-mp="2qmtwp2yc2x2y">
          <h2 className={`text-xl font-bold mb-6 ${dk.textHeading}`} data-id="element-9995" data-mp="2qm1z5ml6epa1">
            Browse Categories
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-9996" data-mp="x4wakk4gnt63">
            {categories.map(category => <div key={category.name} className={`p-6 rounded-xl border shadow-sm hover:shadow-md transition-all cursor-pointer group ${dk.card} ${dk.border}`} data-id="element-9997" data-mp="s2x6t94t6u8m">
                <div className="flex items-start justify-between mb-4" data-id="element-9998" data-mp="113vcrwyt5btu">
                  <div className={`p-3 rounded-lg ${category.color}`} data-id="element-9999" data-mp="1ft19nea6apyk">
                    <category.icon className="h-6 w-6" data-id="element-10000" data-mp="1aug7vaf9vy80" />
                  </div>
                  <ChevronRightIcon className={`h-5 w-5 group-hover:translate-x-1 transition-transform ${dk.textMuted}`} data-id="element-10001" data-mp="1ovsntlufnztw" />
                </div>
                <h3 className={`font-bold text-lg mb-1 ${dk.textHeading}`} data-id="element-10002" data-mp="3qmnnwpvoal6f">
                  {category.name}
                </h3>
                <p className={`text-sm ${dk.textMuted}`} data-id="element-10003" data-mp="3mlu0ryq21ael">
                  {category.articles} articles
                </p>
              </div>)}
          </div>
        </div>

        {/* Popular Articles */}
        <div className={`rounded-2xl p-8 mb-16 border shadow-sm ${dk.card} ${dk.border}`} data-id="element-10004" data-mp="3ghfdmp0c1cdx">
          <h2 className={`text-xl font-bold mb-6 ${dk.textHeading}`} data-id="element-10005" data-mp="32qsljfp2ukqt">
            Popular Articles
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-4" data-id="element-10006" data-mp="37oalwhp13rn7">
            {popularArticles.map((article, idx) => <a key={idx} href="#" className={`flex items-center justify-between group py-3 border-b border-dashed last:border-0 md:last:border-0 md:nth-last-child-2:border-0 ${dk.border}`} data-id="element-10007" data-mp="2xrz63hq8cjo0" data-mpi={idx}>
                <div className="flex items-center gap-3" data-id="element-10008" data-mp="2orsygch7qdwr">
                  <span className={`font-medium group-hover:text-indigo-500 transition-colors ${dk.textPrimary}`} data-id="element-10009" data-mp="i0tk7p2jf9x6">
                    {article.title}
                  </span>
                  <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${dk.surfaceMuted} ${dk.textMuted}`} data-id="element-10010" data-mp="kmapzr11dy27">
                    {article.category}
                  </span>
                </div>
                <ChevronRightIcon className={`h-4 w-4 group-hover:text-indigo-500 transition-colors ${dk.textMuted}`} data-id="element-10011" data-mp="6lzwshc987dv" />
              </a>)}
          </div>
        </div>

        {/* Contact Support */}
        <div className="text-center" data-id="element-10012" data-mp="zbqgr70ki3hw">
          <h2 className={`text-xl font-bold mb-8 ${dk.textHeading}`} data-id="element-10013" data-mp="zcrsldpequ0o">
            Still need help?
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-10014" data-mp="pkvpfo8gsdpl">
            <div className={`p-6 rounded-xl border text-center ${dk.card} ${dk.border}`} data-id="element-10015" data-mp="pihynrkthnnu">
              <div className={`inline-flex p-3 rounded-full mb-4 ${darkMode ? 'bg-indigo-900/30 text-indigo-400' : 'bg-indigo-50 text-indigo-600'}`} data-id="element-10016" data-mp="1i99qkcuunq5d">
                <MailIcon className="h-6 w-6" data-id="element-10017" data-mp="1pmir71jjf66c" />
              </div>
              <h3 className={`font-bold mb-1 ${dk.textHeading}`} data-id="element-10018" data-mp="1ukafllkz4a5s">
                Email Support
              </h3>
              <p className={`text-sm mb-4 ${dk.textSecondary}`} data-id="element-10019" data-mp="1zh0yego5xjuw">
                support@litlaunch.com
              </p>
              <p className={`text-xs font-medium ${dk.textMuted}`} data-id="element-10020" data-mp="24fc54ij5ve8w">
                Response within 24 hours
              </p>
            </div>

            <div className={`p-6 rounded-xl border text-center ${dk.card} ${dk.border}`} data-id="element-10021" data-mp="2dhle33ys6zk6">
              <div className={`inline-flex p-3 rounded-full mb-4 ${darkMode ? 'bg-emerald-900/30 text-emerald-400' : 'bg-emerald-50 text-emerald-600'}`} data-id="element-10022" data-mp="2iaehnoiik1uo">
                <MessageSquareIcon className="h-6 w-6" data-id="element-10023" data-mp="2s8atkij9z0tu" />
              </div>
              <h3 className={`font-bold mb-1 ${dk.textHeading}`} data-id="element-10024" data-mp="2w8l7chdb6vj9">Live Chat</h3>
              <p className={`text-sm mb-4 ${dk.textSecondary}`} data-id="element-10025" data-mp="2w6idfm91ci99">
                Chat with our team
              </p>
              <p className={`text-xs font-medium ${dk.textMuted}`} data-id="element-10026" data-mp="3dihozr6eszud">
                Mon–Fri, 9am–5pm EST
              </p>
            </div>

            <div className={`p-6 rounded-xl border text-center ${dk.card} ${dk.border}`} data-id="element-10027" data-mp="3rhh4a88182dv">
              <div className={`inline-flex p-3 rounded-full mb-4 ${darkMode ? 'bg-amber-900/30 text-amber-400' : 'bg-amber-50 text-amber-600'}`} data-id="element-10028" data-mp="3mjpg9paid0ey">
                <UsersIcon className="h-6 w-6" data-id="element-10029" data-mp="2tvjv51fjl9el" />
              </div>
              <h3 className={`font-bold mb-1 ${dk.textHeading}`} data-id="element-10030" data-mp="38jn14atl4l3e">
                Community Forum
              </h3>
              <p className={`text-sm mb-4 ${dk.textSecondary}`} data-id="element-10031" data-mp="33rdccy47hr2p">
                Ask the community
              </p>
              <p className={`text-xs font-medium ${dk.textMuted}`} data-id="element-10032" data-mp="2g1994ruxjtc8">
                Usually answered within hours
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>;
};
export default HelpCenter;